import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'
import { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

// Ancestor group keys (option values, excluding the value itself) for tree
// grouping — fed to CardList's getGroupAncestors so card groups nest along the
// hierarchy, mirroring the desktop table's nested grouping.
export const getHierarchyGroupAncestors = (
  value: string,
  hierarchy: IHierarchyAccessors,
): string[] =>
  hierarchy
    .getAncestorChain(value)
    .slice(0, -1)
    .map(node => node.value)

// CardList group-config fragment that makes groups nest along the hierarchy and
// sort depth-first. Spread into a card list's groupConfig (mobile cards, picker)
// for a hierarchy field so every card-list surface nests groups identically.
export const hierarchyGroupNesting = (
  hierarchy: IHierarchyAccessors,
): {
  getGroupAncestors: (key: string) => string[]
  sortGroups: (a: string, b: string) => number
} => ({
  getGroupAncestors: (key: string) =>
    getHierarchyGroupAncestors(key, hierarchy),
  sortGroups: (a: string, b: string) => compareHierarchyGroups(a, b, hierarchy),
})

// Same nesting as hierarchyGroupNesting, but siblings follow the property's
// configured option order instead of their labels.
export const hierarchyOptionOrderNesting = (
  hierarchy: IHierarchyAccessors,
): {
  getGroupAncestors: (key: string) => string[]
  sortGroups: (a: string, b: string) => number
} => ({
  getGroupAncestors: (key: string) =>
    getHierarchyGroupAncestors(key, hierarchy),
  sortGroups: (a: string, b: string) =>
    compareHierarchyGroupsByOptionOrder(a, b, hierarchy),
})

// Order group keys depth-first with siblings sorted by own label, matching the
// desktop nested grouping: compares the two values' root→leaf label paths, with
// value tie-breaks so duplicate-label subtrees stay contiguous.
export const compareHierarchyGroups = (
  a: string,
  b: string,
  hierarchy: IHierarchyAccessors,
): number => {
  if (a === b) return 0
  // Pin the unassigned bucket last, like compareGroupsByLabel — its empty
  // ancestor path would otherwise sort it before every real group. Some
  // surfaces (ResourcePicker) key the bucket as '' rather than the sentinel.
  if (!a || a === UNASSIGNED_FILTER_VALUE) return 1
  if (!b || b === UNASSIGNED_FILTER_VALUE) return -1

  // Orphan/unknown values (a record pointing at a removed option) have no
  // ancestor chain; treat them as roots labelled by their own label so they
  // interleave with real roots instead of sorting before every group.
  const chainOf = (value: string) => {
    const chain = hierarchy.getAncestorChain(value)
    return chain.length
      ? chain
      : [{ value, label: hierarchy.getOwnLabel(value) }]
  }
  const chainA = chainOf(a)
  const chainB = chainOf(b)
  const shared = Math.min(chainA.length, chainB.length)
  for (let i = 0; i < shared; i++) {
    const labelComparison = compareLabelsAlphanumeric(
      chainA[i].label,
      chainB[i].label,
    )
    if (labelComparison !== 0) return labelComparison
    if (chainA[i].value !== chainB[i].value) {
      return compareLabelsAlphanumeric(chainA[i].value, chainB[i].value)
    }
  }
  const pathComparison = chainA.length - chainB.length
  if (pathComparison !== 0) return pathComparison
  return compareLabelsAlphanumeric(a, b)
}

const optionRankCache = new WeakMap<IHierarchyAccessors, Map<string, number>>()

const getOptionRanks = (hierarchy: IHierarchyAccessors) => {
  let ranks = optionRankCache.get(hierarchy)
  if (!ranks) {
    const order = new Map<string, number>()
    const visit = (parentValue: string | null) =>
      hierarchy.getChildren(parentValue).forEach(child => {
        if (order.has(child.value)) return
        order.set(child.value, order.size)
        visit(child.value)
      })
    visit(null)
    ranks = order
    optionRankCache.set(hierarchy, ranks)
  }
  return ranks
}

// Order group keys as the options are configured: depth-first, each option
// before its children, siblings in option order. Unknown values follow every
// option by own label; the unassigned bucket stays last.
export const compareHierarchyGroupsByOptionOrder = (
  a: string,
  b: string,
  hierarchy: IHierarchyAccessors,
): number => {
  if (a === b) return 0
  if (!a || a === UNASSIGNED_FILTER_VALUE) return 1
  if (!b || b === UNASSIGNED_FILTER_VALUE) return -1

  const ranks = getOptionRanks(hierarchy)
  const rankA = ranks.get(a)
  const rankB = ranks.get(b)
  if (rankA !== undefined && rankB !== undefined) return rankA - rankB
  if (rankA !== undefined) return -1
  if (rankB !== undefined) return 1
  return (
    compareLabelsAlphanumeric(
      hierarchy.getOwnLabel(a),
      hierarchy.getOwnLabel(b),
    ) || compareLabelsAlphanumeric(a, b)
  )
}
